// import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { KaryvaLogoAnimated3D } from '../KaryvaLogoAnimated3D';

interface PlatformPageProps {
  onNavigate: (page: string) => void;
}

export function PlatformPage({ onNavigate }: PlatformPageProps) {
  const modules = [
    {
      title: 'Executive Command Center',
      description: 'Real-time portfolio oversight with comprehensive KPIs, health metrics, and strategic alignment tracking',
      features: [
        'Portfolio health dashboard',
        'Strategic alignment tracking',
        'Executive-level reporting',
        'Cross-portfolio analytics',
        'Resource utilization metrics',
      ],
    },
    {
      title: 'Portfolio Management',
      description: 'Manage multiple portfolios with comprehensive governance, prioritization, and capacity planning',
      features: [
        'Portfolio configuration',
        'Project prioritization',
        'Capacity planning',
        'Budget tracking',
        'Strategic roadmapping',
      ],
    },
    {
      title: 'Program Workspace',
      description: 'Coordinate complex programs with integrated project tracking, dependency management, and benefit realization',
      features: [
        'Multi-project coordination',
        'Dependency management',
        'Benefit tracking',
        'Program RAID logs',
        'Milestone tracking',
      ],
    },
    {
      title: 'Project Workspace',
      description: 'Complete project management with scope, schedule, budget, quality, and resource management',
      features: [
        '5-gate approval process',
        'Scope & schedule management',
        'Budget & cost tracking',
        'Team collaboration',
        'Document management',
      ],
    },
    {
      title: 'Governance Hub',
      description: 'Centralized governance with approval workflows, change control, and comprehensive audit trails',
      features: [
        'Approval workflows',
        'Change control board',
        'Decision tracking',
        'Compliance monitoring',
        'Audit trail management',
      ],
    },
    {
      title: 'Quality Assurance',
      description: 'Standards-driven QA with compliance tracking, quality audits, and approval workflows',
      features: [
        '6 QA standards frameworks',
        'Compliance tracking',
        'Quality audits',
        'Issue management',
        'Corrective actions',
      ],
    },
    {
      title: 'Reports & Analytics',
      description: 'Comprehensive business intelligence with customizable dashboards and advanced analytics',
      features: [
        'Custom dashboards',
        'Predictive analytics',
        'Trend analysis',
        'Export capabilities',
        'Scheduled reports',
      ],
    },
    {
      title: 'Resource Management',
      description: 'Enterprise resource planning with allocation, capacity management, and skill tracking',
      features: [
        'Resource allocation',
        'Capacity planning',
        'Skill matrix',
        'Utilization tracking',
        'Conflict resolution',
      ],
    },
  ];

  const capabilities = [
    {
      category: 'AI-Powered Intelligence',
      items: [
        'Risk assessment assistant',
        'Quality check automation',
        'Scope definition support',
        'Governance recommendations',
      ],
    },
    {
      category: 'Document Management',
      items: [
        '29 PMI-compliant templates',
        'Version control',
        'Download/edit/upload workflows',
        'Approval routing',
      ],
    },
    {
      category: 'RAID Management',
      items: [
        'Multi-level tracking',
        'Risk mitigation planning',
        'Action item management',
        'Issue escalation',
      ],
    },
    {
      category: 'Customization',
      items: [
        'Client-specific workflows',
        'Custom color schemes',
        'Terminology mapping',
        'Feature configuration',
      ],
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <h1 className="text-5xl mb-6">Comprehensive Platform for Enterprise PPM</h1>
              <p className="text-xl text-[#D5D9DD] leading-relaxed">
                Eight integrated modules delivering complete project portfolio management capabilities,
                built on PMI/PMBOK principles with enterprise-grade security and customization.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DAA520] opacity-20 blur-3xl rounded-full"></div>
                <KaryvaLogoAnimated3D size="hero" darkBg={true} autoPlay={true} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Modules */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">8 Integrated Modules</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Everything you need to manage projects, programs, and portfolios in one unified platform
            </p>
          </div>

          <div className="space-y-8">
            {modules.map((module, idx) => (
              <div
                key={idx}
                className="bg-[#E8E9E9] rounded-lg border border-[#C4CDD5] overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-2xl text-[#2A3B4C] mb-2">{module.title}</h3>
                      <p className="text-[#556B7C] leading-relaxed">{module.description}</p>
                    </div>
                    <div className="ml-4 w-12 h-12 bg-[#DAA520] rounded-lg flex items-center justify-center text-white flex-shrink-0">
                      {idx + 1}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
                    {module.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm text-[#556B7C]">
                        <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Advanced Capabilities</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Powered by cutting-edge technology and industry best practices
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((cap, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-[#C4CDD5] p-6">
                <h3 className="text-lg text-[#2A3B4C] mb-4 pb-3 border-b border-[#C4CDD5]">
                  {cap.category}
                </h3>
                <ul className="space-y-3">
                  {cap.items.map((item, iIdx) => (
                    <li key={iIdx} className="flex items-start gap-2 text-sm text-[#556B7C]">
                      <CheckCircle className="w-4 h-4 text-[#DAA520] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PMI Compliance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] rounded-2xl p-12 text-white">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl mb-6">PMI/PMBOK Compliant by Design</h2>
                <p className="text-[#D5D9DD] mb-8 leading-relaxed">
                  Built on Project Management Institute standards with comprehensive governance frameworks,
                  approval gates, and quality assurance processes that align with PMBOK best practices.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#DAA520] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium mb-1">5-Gate Approval Process</div>
                      <div className="text-sm text-[#A8B5C3]">
                        Initiation, Planning, Execution, Monitoring & Closing gates
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#DAA520] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium mb-1">29 Document Templates</div>
                      <div className="text-sm text-[#A8B5C3]">
                        Complete set of PMI-aligned project documents
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#DAA520] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium mb-1">Knowledge Area Coverage</div>
                      <div className="text-sm text-[#A8B5C3]">
                        All 10 PMBOK knowledge areas supported
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20">
                <h3 className="text-xl mb-6">Document Templates Included</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-[#D5D9DD]">• Project Charter</div>
                  <div className="text-[#D5D9DD]">• Business Case</div>
                  <div className="text-[#D5D9DD]">• Scope Statement</div>
                  <div className="text-[#D5D9DD]">• WBS Dictionary</div>
                  <div className="text-[#D5D9DD]">• Schedule Baseline</div>
                  <div className="text-[#D5D9DD]">• Cost Baseline</div>
                  <div className="text-[#D5D9DD]">• Quality Plan</div>
                  <div className="text-[#D5D9DD]">• Risk Register</div>
                  <div className="text-[#D5D9DD]">• RACI Matrix</div>
                  <div className="text-[#D5D9DD]">• Lessons Learned</div>
                  <div className="text-[#D5D9DD] col-span-2 text-center mt-2">
                    ... and 19 more templates
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl text-[#2A3B4C] mb-6">
            See Karyva in Action
          </h2>
          <p className="text-xl text-[#556B7C] mb-8">
            Schedule a personalized demo to explore how Karyva can transform your project portfolio management
          </p>
          <Button
            onClick={() => onNavigate('contact')}
            className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg"
          >
            Contact Karyva Team
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}